import { convertCelsius, tempUnitSuffix } from '@/utils/conversions'

import {
  ApiTimestamp,
  NumericSample,
  RowStatus,
  ShipmentState,
  TrackerHistoryPoint,
  TrackerShipmentRow,
  TrackerStatus,
} from './types'

export type TempUnits = 'Imperial' | 'Metric'

const STALE_LOCATION_MS = 6 * 60 * 60 * 1000 // 6h without a fix → stale
const LOW_BATTERY_PERCENT = 20

/** Epoch millis from an API timestamp (string or already-converted Date), or null. */
export const tsToMillis = (v: ApiTimestamp | undefined): number | null => {
  if (!v) return null
  const ms = new Date(v).getTime()
  return Number.isNaN(ms) ? null : ms
}

/**
 * Derive a tracker's status (used for marker color + list icon). Trackers have no
 * alarms in v1: a stale location or low battery is the only signal.
 */
export const getTrackerStatus = (tracker: {
  locationTs: ApiTimestamp
  batteryPercent: number | null
}): TrackerStatus => {
  const locationMs = tsToMillis(tracker.locationTs)
  if (locationMs == null || Date.now() - locationMs > STALE_LOCATION_MS) return 'stale'
  if (tracker.batteryPercent != null && tracker.batteryPercent <= LOW_BATTERY_PERCENT) return 'warning'
  return 'ok'
}

/**
 * Row-level status: shipments with no tracker are 'planned' (list-only, no
 * sensor data), rows frozen at shipment end are 'completed' (staleness vs now
 * would always read stale), otherwise the live tracker status.
 */
export const getRowStatus = (row: TrackerShipmentRow): RowStatus => {
  if (row.trackerId == null) return 'planned'
  if (row.asOf === 'shipmentEnd') return 'completed'
  return getTrackerStatus(row)
}

/**
 * Stable numeric mapbox feature id for a row. Rows are shipments or unassigned
 * trackers; the parity split keeps the two id spaces from colliding.
 */
export const rowId = (row: TrackerShipmentRow): number =>
  row.shipmentId != null ? row.shipmentId * 2 : (row.trackerId as number) * 2 + 1

/** Stable string key for react lists / table rows. */
export const rowKey = (row: TrackerShipmentRow): string =>
  row.shipmentId != null ? `s-${row.shipmentId}` : `t-${row.trackerId}`

// Maps a status to the marker image base name registered by addTrackerMarkerImages.
// 'planned' rows have no location so never render a marker; completed rows reuse
// the gray (offline) marker to read as historical.
export const statusToMarkerImage = (status: RowStatus): string => {
  switch (status) {
    case 'stale':
    case 'completed':
      return 'offline-marker'
    case 'warning':
      return 'warning-marker'
    default:
      return 'default-marker'
  }
}

export const statusColor = (status: RowStatus): string => {
  switch (status) {
    case 'stale':
    case 'completed':
      return '#999999'
    case 'warning':
      return '#eab308'
    case 'planned':
      return '#8b5cf6'
    default:
      return '#3b82f6'
  }
}

export const SHIPMENT_STATE_LABELS: Record<ShipmentState, string> = {
  planned: 'Planned',
  active: 'Active',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

/** daisyUI badge color per shipment status (list, quick view, modal). */
export const SHIPMENT_STATE_BADGE_CLASS: Record<ShipmentState, string> = {
  planned: 'badge-info',
  active: 'badge-success',
  completed: 'badge-neutral',
  cancelled: 'badge-error',
}

export const formatTemp = (celsius: number | null, units: TempUnits): string =>
  celsius == null ? '—' : `${convertCelsius(celsius, units).toFixed(1)}${tempUnitSuffix(units)}`

/** Shipment min–max range in the user's units, e.g. "2–8°C" / "35.6–46.4°F". */
export const formatTempRange = (minC: number | null, maxC: number | null, units: TempUnits): string =>
  minC != null && maxC != null
    ? `${Number(convertCelsius(minC, units).toFixed(1))}–${Number(convertCelsius(maxC, units).toFixed(1))}${tempUnitSuffix(units)}`
    : '—'

export const formatPercent = (value: number | null): string => (value == null ? '—' : `${Math.round(value)}%`)

export const formatLux = (lux: number | null): string => (lux == null ? '—' : `${Math.round(lux).toLocaleString()} lux`)

/**
 * Correlate each location fix with the temperature reading nearest in time.
 * Temperature and location come from separate history tables (different
 * timestamps), so we binary-search the sorted temperature series for the closest
 * sample to each fix — mirroring the nearest-time lookup in the tracker history
 * chart (useTrackerChartOptions.findLocation). Fixes with no temperature series
 * at all get temperatureCelsius = null.
 */
export const correlateLocationTemps = (
  location: { time: string; latitude: number; longitude: number }[],
  temperature: NumericSample[]
): TrackerHistoryPoint[] => {
  const temps = temperature.map((s) => ({ t: new Date(s.time).getTime(), v: s.value }))
  const nearestTemp = (ms: number): number | null => {
    if (!temps.length) return null
    let lo = 0
    let hi = temps.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (temps[mid].t < ms) lo = mid + 1
      else hi = mid
    }
    let best = lo
    if (lo > 0 && Math.abs(temps[lo - 1].t - ms) < Math.abs(temps[lo].t - ms)) best = lo - 1
    return temps[best].v
  }
  return location.map((l) => ({
    time: l.time,
    latitude: l.latitude,
    longitude: l.longitude,
    temperatureCelsius: nearestTemp(new Date(l.time).getTime()),
  }))
}

// Flatten a row into a lowercased search blob (shipment, label, serial, imei, route).
export const trackerSearchBlob = (t: TrackerShipmentRow): string =>
  [
    t.shipmentName,
    t.shipmentState,
    t.originName,
    t.destinationName,
    t.label,
    t.vendor,
    t.serialNumber,
    t.imei,
    t.modelNumber,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
